import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { DataSet, Network } from 'vis-network/standalone';
// Note: intentionally NOT importing 'vis-network/styles/vis-network.css' here —
// its package.json 'exports' map for './styles/*' lists the 'default' condition
// before 'types', which is spec-invalid and webpack 5 rejects it ("Default
// condition should be last one"). The graph itself is canvas-rendered and needs
// no CSS; that stylesheet only styles the (unused here) tooltip/manipulation-UI
// chrome. Add it back (e.g. via a webpack resolve.alias to the physical file)
// only if hover tooltips end up looking unstyled in practice.

/**
 * NetworkTopologyDisplay Component
 * Renders an interactive network topology / physical hierarchy diagram
 * emitted by the RenderTopology agent tool.
 *
 * Unlike ChartDisplay (declarative Plotly JSX), vis-network is an imperative
 * API — the graph is built/torn down in a ref-backed useEffect rather than
 * rendered as JSX.
 *
 * ✅ Memoized to prevent re-renders (and graph rebuilds) when topology data
 * hasn't changed.
 */

// Edges are {from: parentId, to: childId}, per the RenderTopology SSE payload.
// Builds the parent→children adjacency plus the root set (nodes nothing
// points at) once, shared by both the height calculation and collapse logic
// below so we don't walk the tree twice with slightly different code.
function buildTreeIndex(nodesIn, edgesIn) {
    const childrenOf = new Map();
    const hasParent = new Set();
    (edgesIn || []).forEach((e) => {
        if (!childrenOf.has(e.from)) childrenOf.set(e.from, []);
        childrenOf.get(e.from).push(e.to);
        hasParent.add(e.to);
    });
    const roots = nodesIn.filter((n) => !hasParent.has(n.id)).map((n) => n.id);
    return { childrenOf, roots: roots.length > 0 ? roots : [nodesIn[0].id] };
}

// Largest sibling count at any single depth — used to size the container
// tall enough that a bushy level doesn't overlap (see the LR-layout note
// below for why vertical, not horizontal, is the axis that needs the room).
function maxSiblingsAtAnyLevel(roots, childrenOf) {
    let frontier = roots;
    const seen = new Set(frontier);
    let max = frontier.length;
    while (frontier.length > 0) {
        const next = [];
        frontier.forEach((id) => {
            (childrenOf.get(id) || []).forEach((childId) => {
                if (!seen.has(childId)) {
                    seen.add(childId);
                    next.push(childId);
                }
            });
        });
        max = Math.max(max, next.length);
        frontier = next;
    }
    return max;
}

function NetworkTopologyDisplay({ topologyData }) {
    const containerRef = useRef(null);
    const networkRef = useRef(null);
    // Toolbar buttons live in JSX outside the effect, but the functions they
    // call (zoom, fit, expand/collapse-all) close over the Network/DataSet
    // instances created inside it — this ref is the bridge between the two
    // without forcing a re-render (and graph rebuild) on every click.
    const controlsRef = useRef({});
    const history = useHistory();
    const [renderError, setRenderError] = useState(null);

    useEffect(() => {
        if (!topologyData || !containerRef.current) {
            return undefined;
        }

        const nodesIn = topologyData.nodes;
        const edgesIn = topologyData.edges;

        if (!Array.isArray(nodesIn) || nodesIn.length === 0) {
            return undefined;
        }

        try {
            setRenderError(null);

            const { childrenOf, roots } = buildTreeIndex(nodesIn, edgesIn);
            const baseLabelOf = new Map(nodesIn.map((n) => [n.id, n.label]));

            // Bushy levels (many leaf siblings under one parent) used to overlap
            // because they were forced into one horizontal band inside a
            // chat-width-constrained container. Laying the tree out left-to-right
            // instead moves that "many siblings" axis to vertical, which has no
            // such width ceiling — we just size the container to fit the busiest
            // level so nothing has to be squeezed.
            const busiestLevel = maxSiblingsAtAnyLevel(roots, childrenOf);
            containerRef.current.style.height = `${Math.min(
                900,
                Math.max(420, busiestLevel * 46 + 80)
            )}px`;

            const nodes = new DataSet(
                nodesIn.map((n) => ({
                    id: n.id,
                    label: n.label,
                    title: [n.type, n.status].filter(Boolean).join(' — ') || undefined,
                    color: {
                        background: n.color || '#9E9E9E',
                        border: n.color || '#9E9E9E',
                        highlight: {
                            background: n.color || '#9E9E9E',
                            border: '#1A1A1A',
                        },
                    },
                    font: { color: '#1a1a1a' },
                    shape: 'box',
                    margin: 10,
                }))
            );

            // Explicit ids (rather than letting vis-network generate random ones)
            // so collapse/expand can address a specific edge later via update().
            const edges = new DataSet(
                (edgesIn || []).map((e) => ({ id: `${e.from}::${e.to}`, from: e.from, to: e.to }))
            );

            const network = new Network(
                containerRef.current,
                { nodes, edges },
                {
                    layout: {
                        hierarchical: {
                            // LR (left-to-right) instead of UD (top-down): depth now
                            // runs along X (few levels, plenty of chat-panel width)
                            // and siblings stack along Y, which the dynamic container
                            // height above sizes to fit — see maxSiblingsAtAnyLevel.
                            direction: 'LR',
                            sortMethod: 'directed',
                            levelSeparation: 260,
                            nodeSpacing: 70,
                        },
                    },
                    interaction: {
                        dragNodes: true,
                        dragView: true,
                        zoomView: true,
                        hover: true,
                    },
                    physics: {
                        hierarchicalRepulsion: { nodeDistance: 70 },
                    },
                    edges: {
                        arrows: { to: { enabled: true, scaleFactor: 0.6 } },
                        color: { color: '#9CA3AF', highlight: '#CD040B' },
                        smooth: { type: 'cubicBezier', forceDirection: 'horizontal' },
                    },
                    nodes: {
                        borderWidth: 2,
                    },
                }
            );

            // ── Collapse / expand ────────────────────────────────────────────
            // Positions are computed once by the hierarchical layout above and
            // never recomputed on toggle (physics is frozen once stabilized) —
            // collapsing just hides a subtree in place; expanding reveals it at
            // its already-computed coordinates. No layout jank either way.
            const collapsedIds = new Set();

            function countDescendants(id) {
                let count = 0;
                let frontier = childrenOf.get(id) || [];
                const seen = new Set(frontier);
                count += frontier.length;
                while (frontier.length > 0) {
                    const next = [];
                    frontier.forEach((cid) => {
                        (childrenOf.get(cid) || []).forEach((gcid) => {
                            if (!seen.has(gcid)) {
                                seen.add(gcid);
                                next.push(gcid);
                                count += 1;
                            }
                        });
                    });
                    frontier = next;
                }
                return count;
            }

            function computeVisible() {
                const visible = new Set(roots);
                let frontier = roots;
                while (frontier.length > 0) {
                    const next = [];
                    frontier.forEach((id) => {
                        if (collapsedIds.has(id)) return; // don't descend past a collapsed node
                        (childrenOf.get(id) || []).forEach((childId) => {
                            if (!visible.has(childId)) {
                                visible.add(childId);
                                next.push(childId);
                            }
                        });
                    });
                    frontier = next;
                }
                return visible;
            }

            function labelFor(id) {
                const base = baseLabelOf.get(id);
                if (!childrenOf.has(id)) return base;
                if (collapsedIds.has(id)) return `${base} ▸ (${countDescendants(id)})`;
                return `${base} ▾`;
            }

            function applyVisibility() {
                const visible = computeVisible();
                nodes.update(nodesIn.map((n) => ({ id: n.id, hidden: !visible.has(n.id), label: labelFor(n.id) })));
                edges.update(
                    (edgesIn || []).map((e) => ({
                        id: `${e.from}::${e.to}`,
                        hidden: !(visible.has(e.from) && visible.has(e.to)),
                    }))
                );
                network.fit({ nodes: Array.from(visible), animation: { duration: 300 } });
            }

            function toggleCollapse(id) {
                if (collapsedIds.has(id)) {
                    collapsedIds.delete(id);
                } else {
                    collapsedIds.add(id);
                }
                applyVisibility();
            }

            // Set the initial ▾/▸ labels (nothing collapsed yet) before first paint.
            applyVisibility();

            // Freeze physics once the layout settles so dragging one node
            // doesn't perpetually jostle the rest of the graph, then zoom-to-fit
            // so the full tree is visible without a manual scroll/drag first —
            // matters most for the 900px-capped tall trees.
            //
            // vis-network's hierarchical layout also pins each node's `fixed`
            // flag along the level axis while it computes/stabilizes positions
            // (fixed.x for our LR direction, since x is depth) — that flag blocks
            // manual dragging on that axis too, not just physics, which is why
            // nodes could be dragged vertically but not horizontally. Clear it
            // once layout has settled so both axes are freely draggable.
            network.once('stabilized', () => {
                network.setOptions({ physics: false });
                nodes.update(nodesIn.map((n) => ({ id: n.id, fixed: { x: false, y: false } })));
                network.fit({ animation: false });
            });

            // Parent nodes (have children) toggle collapse/expand on click — the
            // ▾/▸ suffix on their label is the affordance. Leaf nodes keep the
            // prior click-to-navigate behavior, since they have nothing to collapse.
            network.on('click', (params) => {
                if (!params.nodes || params.nodes.length === 0) return;
                const nodeId = params.nodes[0];
                if (childrenOf.has(nodeId)) {
                    toggleCollapse(nodeId);
                } else {
                    // Plain-path deeplink navigation, matching the convention
                    // used elsewhere in this app (no query params on any route).
                    history.push('/monitoring/inventory');
                }
            });

            controlsRef.current = {
                zoomIn: () => network.moveTo({ scale: network.getScale() * 1.2, animation: { duration: 200 } }),
                zoomOut: () => network.moveTo({ scale: network.getScale() / 1.2, animation: { duration: 200 } }),
                fitView: () => network.fit({ animation: { duration: 300 } }),
                expandAll: () => {
                    collapsedIds.clear();
                    applyVisibility();
                },
                collapseAll: () => {
                    nodesIn.forEach((n) => {
                        if (childrenOf.has(n.id)) collapsedIds.add(n.id);
                    });
                    applyVisibility();
                },
            };

            networkRef.current = network;
        } catch (error) {
            console.error('Topology rendering error:', error);
            setRenderError(error);
        }

        return () => {
            controlsRef.current = {};
            if (networkRef.current) {
                networkRef.current.destroy();
                networkRef.current = null;
            }
        };
    }, [topologyData, history]);

    function handleDownload() {
        const canvas = containerRef.current && containerRef.current.querySelector('canvas');
        if (!canvas) return;
        const safeTitle = (topologyData?.title || 'network-topology').replace(/[^a-z0-9-_]+/gi, '_');
        const link = document.createElement('a');
        link.download = `${safeTitle}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
    }

    if (!topologyData || !Array.isArray(topologyData.nodes) || topologyData.nodes.length === 0) {
        return null;
    }

    if (renderError) {
        return (
            <div className="topology-display topology-error">
                <p>⚠️ Error rendering topology diagram. Check console for details.</p>
            </div>
        );
    }

    return (
        <div className="topology-display">
            <div className="topology-header">
                <span className="topology-icon">🕸️</span>
                <span className="topology-label">{topologyData.title || 'Network Topology'}</span>
                <span className="topology-hint">click a node with ▾/▸ to expand or collapse it</span>
            </div>
            <div className="topology-canvas-area">
                <div className="topology-wrapper" ref={containerRef} />
                <div className="topology-toolbar">
                    <button type="button" title="Zoom in" onClick={() => controlsRef.current.zoomIn?.()}>+</button>
                    <button type="button" title="Zoom out" onClick={() => controlsRef.current.zoomOut?.()}>−</button>
                    <button type="button" title="Fit to view" onClick={() => controlsRef.current.fitView?.()}>⤢</button>
                    <button type="button" className="wide" title="Expand all nodes" onClick={() => controlsRef.current.expandAll?.()}>Expand</button>
                    <button type="button" className="wide" title="Collapse all nodes" onClick={() => controlsRef.current.collapseAll?.()}>Collapse</button>
                    <button type="button" title="Download as PNG" onClick={handleDownload}>⬇</button>
                </div>
            </div>
        </div>
    );
}

// ✅ Memoize component: Only re-render when topologyData actually changed
export default React.memo(NetworkTopologyDisplay, (prevProps, nextProps) => {
    return JSON.stringify(prevProps.topologyData) === JSON.stringify(nextProps.topologyData);
});
